import React, { useState } from 'react';
import useSWR from 'swr';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Loader2, Camera, CloudRain } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { formatDate } from '@/lib/format';
import * as S from '@/lib/site/service';
import { SchemaNotice, Loading, StatusPill, Field, ReadinessGate } from '@/components/common/F1States';
import { useSchemaReady } from '@/lib/schemaReady';
import { Select } from '@/components/purchasing/LineEditor';

export default function SiteDiaryPage() {
  const nav = useNavigate();
  const { organisationId } = useAuth();
  const readiness = useSchemaReady(organisationId && 'diary', ['site_diaries', 'diary_labour', 'diary_photos']);
  const ready = readiness.state === 'ready';
  const projects = useSWR(organisationId && ready ? ['projects-lookup', organisationId] : null, () => S.listProjects(organisationId));
  const [projectId, setProjectId] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const diaries = useSWR(organisationId && ready ? ['diaries', organisationId, projectId] : null, () => S.listDiaries(organisationId, projectId || null));
  const open = async () => {
    if (!projectId) return toast.error('Select a project');
    setBusy(true);
    try { const existing = await S.findDiary(projectId, date); const row = existing || await S.createDiary({ organisation_id: organisationId, project_id: projectId, diary_date: date }); nav(`/site-diary/${row.id}`); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <div data-testid="site-diary-page" className="p-4 md:p-8 max-w-5xl mx-auto">
      <PageHeader title="Site Diary" subtitle="One report per project per day — labour on site, work completed, deliveries, weather, delays, instructions, photos" />
      {!ready ? <ReadinessGate readiness={readiness} module="Site Diary" migration="so1_site_operations_migration.sql" testId="diary-migration-pending" /> : (
        <>
          <div className="rounded-lg border border-border bg-white p-3 grid grid-cols-1 sm:grid-cols-3 gap-2 items-end mb-4" data-testid="diary-open-form">
            <Field label="Project"><Select testId="diary-project" value={projectId} onChange={setProjectId} blank="All projects / select to open" options={(projects.data || []).map((p) => [p.id, `${p.project_code} · ${p.name}`])} /></Field>
            <Field label="Date"><Input data-testid="diary-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} /></Field>
            <Button data-testid="diary-open" onClick={open} disabled={busy || !projectId} className="bg-bronze hover:bg-bronze-hover text-white">{busy ? <Loader2 size={14} className="animate-spin" /> : <><Plus size={14} className="mr-1" /> Open today's diary</>}</Button>
          </div>
          {diaries.error && <SchemaNotice error={diaries.error} migration="so1_site_operations_migration.sql" />}
          <div className="rounded-lg border border-border bg-white divide-y divide-border/60" data-testid="diary-list">
            {diaries.isLoading ? <Loading /> : !(diaries.data || []).length ? <div className="p-8 text-center text-sm text-muted-foreground" data-testid="diary-empty">No diary entries yet.</div> : diaries.data.map((d) => (
              <Link key={d.id} to={`/site-diary/${d.id}`} className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-ivory/60" data-testid={`diary-row-${d.id}`}>
                <div><div className="font-medium text-charcoal">{formatDate(d.diary_date)} · {d.projects?.project_code}</div><div className="text-xs text-muted-foreground line-clamp-1">{d.work_completed || 'No summary yet'}</div></div>
                <div className="flex items-center gap-3 text-xs text-muted-foreground">{d.weather && <span className="inline-flex items-center gap-1"><CloudRain size={12} /> {d.weather}</span>}<span>{d.diary_labour?.length || 0} crews</span><span className="inline-flex items-center gap-1"><Camera size={12} /> {d.diary_photos?.length || 0}</span><StatusPill value={d.status} /></div>
              </Link>))}
          </div>
        </>
      )}
    </div>
  );
}
